import moment from 'moment-timezone/moment-timezone';

import { getLogger } from 'wanderu-logger';
import { createChainableTypeChecker } from 'wanderu-utils';

import subsettedTZData from '../../../../express/tzData/subsettedTZData.json';

moment.tz.load(subsettedTZData);

const logger = getLogger('i18nMoment');

async function setLocale(locale) {
    if (!locale) {
        return undefined;
    }

    try {
        return moment.locale(locale);
    } catch (ex) {
        if (!locale.includes('-')) {
            logger.error('setLocale', ex);

            return undefined;
        }

        logger.info('setLocale', ex.toString());

        return moment.locale(locale.split('-')[0].toLowerCase());
    }
}

/**
 * @param {String} locale
 * @return {Promise<void>}
 */
function fetchLocaleData(locale) {
    // en and en-us are built into Moment; there's no external locale file
    if (locale.toLowerCase() === 'en' || locale.toLowerCase() === 'en-us') {
        return Promise.resolve();
    }

    return import(`moment/locale/${locale.toLowerCase()}`)
        .catch(() => {
            if (!locale.includes('-')) {
                throw new Error(`unsupported locale ${locale}`);
            }

            return fetchLocaleData(locale.split('-')[0]);
        });
}

/**
 * return the localized human-readable day of the week
 *
 * @return {String}
 */
function shortDayAndWeekday(locale = 'en-us') {
    // Currently, this function is called by moment so `this` is a moment object
    // and needs to be valid before it can be formatted.
    if (!!global.Intl && this.isValid()) {
        const MILLISECONDS_PER_MINUTE = 60 * 1000;
        const options = {
            day: 'numeric',
            month: 'short',
            timeZone: 'UTC',
            weekday: 'short',
        };

        const offset = this.utcOffset();
        const datetime = this.valueOf();
        const datetimeWithOffset = datetime + (offset * MILLISECONDS_PER_MINUTE);

        return new Intl.DateTimeFormat(locale, options).format(datetimeWithOffset);
    }

    return this.format('llll') /* e.g. 'Thu, Sep 8, 2018 5:59pm' or 'Lun, 8 Mar 2018 1759' */
        .replace(this.format('LT'), '').trim() /* e.g. '5:59pm' or '1759' */
        .replace(this.format('Y'), '')
        .trim() /* e.g. '2018' */
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .trim(); /* e.g. 'Thu Sep 8' or 'Lun 8 Mar' */
}

function timeAndDayAndMonth() {
    return this.format('llll')
        .replace(this.format('Y'), '').trim();
    // results in locale aware formatting: Mon, Jun 22, 12:11 PM or lun., 22 de jun. de 12:11
}

/**
 * generates a string with wday, mday, month, and year in localized order
 *
 * @return {String}
 */
function shortDateTimestamp() {
    const longString = this.format('llll'); /* e.g. `Thu, Sep 4, 1986 8:30 PM` */
    const shortString = this.format('LT'); /* e.g. `8:30 PM` */

    return longString.replace(shortString, '').trim(); /* e.g. `Thu, Sep 4, 1986` */
}

function toShortTime() {
    const time = this.format('LT'); /* e.g. `8:30 PM` */
    return time.replace(' ', '').toLowerCase(); /* e.g. `8:30pm` */
}

(() => {
    moment.fn.shortDateTimestamp = shortDateTimestamp;
    moment.fn.toShortTime = toShortTime;
    moment.fn.shortDayAndWeekday = shortDayAndWeekday;
    moment.fn.timeAndDayAndMonth = timeAndDayAndMonth;
})();

const i18nMoment = moment;

function i18nMomentPropCheck(props, propName, componentName = 'ANONYMOUS') {
    const value = props[propName];
    if (!value || moment.isMoment(value)) {
        return undefined;
    }

    return new Error(`${propName} in ${componentName} is not an instance of i18nMoment (moment)`);
}

const i18nMomentPropType = createChainableTypeChecker(i18nMomentPropCheck);

export {
    fetchLocaleData,
    i18nMoment,
    i18nMomentPropType,
    setLocale,
    shortDayAndWeekday,
    timeAndDayAndMonth,
};

export default i18nMoment;
